<template>
  <app-card v-if="isItemVisible" interactive class="menu-item" @click="showModal(itemCount || menuItem.quantity)">
    <div class="menu-item__content">
      <div>
        <h3 class="menu-item__title">
          {{ menuItem.name }}
        </h3>
        <div v-if="isDescriptionVisible" class="menu-item__description">
          {{ menuItem.description }}
        </div>
        <menu-item-attributes :menu-item="menuItem"></menu-item-attributes>
      </div>
      <div>
        <div class="menu-item__price">
          <span>{{ prependUsdSign(menuItem.cost) }}</span>
          <template v-if="menuItem.calories || parseInt(menuItem.calories) === 0">
            <span class="menu-item__calorie-value">
              {{ menuItem.calories }}
              {{ menuItem.caloriesLabel || "Cals" }}
            </span>
          </template>
        </div>
      </div>
    </div>
    <div class="menu-item__image-container">
      <menu-item-image class="menu-item__thumb" :images="menuItem.images" :is-background="true" :full-res-only="false" />
      <menu-item-counter :menu-item="menuItem"></menu-item-counter>
      <menu-item-favorite v-if="isLoginAllowed" :menu-item="menuItem"></menu-item-favorite>
    </div>
  </app-card>
</template>

<script>
import MenuItemCounter from "../menu-item-counter/MenuItemCounter";
import MenuItemFavorite from "../menu-item-favorite/MenuItemFavorite";
import MenuItemAttributes from "../menu-item-attributes/MenuItemAttributes";
import MenuItemImage from "../menu-item-image/MenuItemImage";
import { mapGetters } from "vuex";
import AppCard from "@/components/base/app-card/AppCard";
import { trackProductClicks, OAPI_API_TYPE, prependUsdSign, DISPLAY } from "@/utils";
import VendorMenuModal from "@/components/modal/vendor-menu-modal/VendorMenuModal";

export default {
  name: "VendorMenuItemCustom",
  components: { MenuItemAttributes, MenuItemCounter, MenuItemFavorite, AppCard, MenuItemImage },
  props: {
    orderId: Number,
    menuItem: Object,
    itemCountUpdated: Function
  },
  data() {
    return {
      itemCount: this.menuItem.quantity || 1
    };
  },
  created() {
    if (this.itemCountUpdated && this.orderId) this.itemCountUpdated(this.orderId, this.menuItem.id, this.itemCount);
  },
  computed: {
    ...mapGetters({
      isLoginAllowed: "venueSettings/isLoginAllowed",
      APIType: "venueSettings/APIType",
      isVendorOpen: "vendor/isActiveVendorOpen",
      getShowHideStatus: "vendor/getShowHideStatus",
      activeVendor: "vendor/getActiveVendor",
      getValidTodayPickupTimes: "vendor/getValidPickupTimesForTodayByVendorId"
    }),
    isDescriptionVisible() {
      return this.menuItem.showDesc && this.menuItem.description;
    },
    isItemVisible() {
      return this.getShowHideStatus(this.menuItem);
    }
  },
  methods: {
    prependUsdSign,
    showModal: function (itemQuantity = null) {
      if (this.isAddToCartEnable()) {
        this.$modal.show(
          VendorMenuModal,
          {
            menuItem: this.menuItem,
            itemQuantity: itemQuantity
          },
          {
            ...this.getModalProps()
          }
        );

        trackProductClicks(this.menuItem);
      }
    },
    isAddToCartEnable() {
      const pickupTimeEnabled = this.activeVendor.pickupTimeSelectionEnabled;

      if (this.isVenueOrderAheadAllowed() && pickupTimeEnabled && this.isVendorOpen) {
        return this.isAnyPickupAvailable(this.activeVendor.id);
      }
      return this.isVendorOpen;
    },
    /**
     * This method returns if the venue allow order ahead feature
     *
     * @return {boolean}
     */
    isVenueOrderAheadAllowed() {
      return this.APIType === OAPI_API_TYPE;
    },
    /**
     * This method indicates if the vendor is open or has future pickup time slots available
     *
     * @param {number} vendorId
     * @return {boolean}
     */
    isAnyPickupAvailable(vendorId) {
      return !!this.getValidTodayPickupTimes(vendorId)?.length;
    },
    getModalProps: function () {
      if (window.innerWidth <= DISPLAY.minDesktopWidth) {
        return {
          name: "item",
          position: "absolute",
          top: "0",
          left: "0"
        };
      }
      return {
        name: "item",
        height: "auto",
        width: 500
      };
    }
  }
};
</script>

<style scoped lang="scss">
.menu-item {
  position: relative;
  display: flex;
  flex-basis: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background-color: $group-1-color-2;
  font-family: $group-1-font-family-1;
  height: auto;
  min-height: 120px;
  flex-direction: row;
  width: 100%;

  &__image-container {
    display: flex;
    position: relative;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
    margin-left: ($spacer * 0.5);
    margin-right: $spacer;
    min-height: 104px;
  }

  &__title {
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: $group-3-color-2;
    margin-bottom: 4px;
    margin-top: 4px;
  }

  &__description {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: normal;
    color: $group-3-color-3;
    word-break: break-word;
    overflow: hidden;
    margin-bottom: 6px;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__calorie-value {
    margin-left: 14px;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: normal;
    color: $group-3-color-3;
  }

  &__price {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    color: $group-3-color-4;
    padding-bottom: 8px;
  }

  &__cta {
    display: flex;
    flex-wrap: nowrap;
  }

  &__thumb {
    background-size: cover;
    padding-right: 0 !important;
  }

  &__count-stepper {
    border-radius: 3px;
    border: solid 1px #373737;
    width: 126px;
    height: 40px;
    display: flex;
    align-items: center;
    margin: 18px 0;
    justify-content: space-evenly;
  }

  &__btn {
    box-sizing: border-box;
    font-size: 16px;
    border: none;
    outline: none;
    cursor: pointer;

    &_add-to-cart {
      flex-basis: 125px;
      min-width: 65px;
      padding: 10px 0;
      color: white;
      border-radius: 20px;
      background-color: darkslategrey;
    }

    &_customize {
      padding: 0;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      font-weight: bold;
      color: darkslategrey;
      background-color: transparent;
    }

    &_modify-count {
      background-color: white;
    }
  }
}
</style>
